import { useState, useEffect } from 'react'
import Card, { CardHeader, CardContent } from '../ui/Card'
import Button from '../ui/Button'
import Input from '../ui/Input'
import LoadingState from '../ui/LoadingState'
import EmptyState from '../ui/EmptyState'
import { reporteService } from '../../services/reporteService'
import Chart from 'react-apexcharts'
import {
  createArtemisAxisOptions,
  createArtemisBaseOptions,
  getArtemisChartTokens,
  getArtemisPalette,
} from '../../utils/artemisApexTheme'
import { Calendar, Download, RefreshCw, AlertCircle, CheckCircle, Clock, XCircle } from 'lucide-react'

export default function ReporteCitas({ idClinica = null }) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [data, setData] = useState(null)
  const [fechaInicio, setFechaInicio] = useState(
    new Date(new Date().setFullYear(new Date().getFullYear() - 1)).toISOString().split('T')[0]
  )
  const [fechaFin, setFechaFin] = useState(new Date().toISOString().split('T')[0])

  useEffect(() => {
    // Solo hacer fetch si idClinica está definido (incluyendo 0) o si es null explícitamente después de que el usuario se haya cargado
    if (idClinica !== undefined) {
      fetchData()
    }
  }, [fechaInicio, fechaFin, idClinica])

  const fetchData = async () => {
    try {
      setLoading(true)
      setError(null)
      const params = {
        fecha_inicio: fechaInicio,
        fecha_fin: fechaFin
      }
      // IMPORTANTE: Solo agregar id_clinica si es un número válido mayor que 0 (no null, no undefined, no 0)
      // null y undefined significan "dejar que el backend lo resuelva automáticamente"
      if (idClinica != null && idClinica !== undefined && !isNaN(Number(idClinica)) && Number(idClinica) > 0) {
        params.id_clinica = Number(idClinica)
      }
      
      // Debug: verificar qué parámetros se están enviando
      console.log('🔍 ReporteCitas - Parámetros enviados:', { idClinica, params })
      
      const response = await reporteService.getReporteCitas(params)
      if (response.success) {
        setData(response.data)
      } else {
        setError(response.error || 'Error al cargar reporte')
      }
    } catch (err) {
      setError(err.message || 'Error de conexión')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return <LoadingState message="Cargando reporte de citas..." size="lg" />
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <div className="text-center py-8">
            <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
            <p className="text-red-700 dark:text-red-400">{error}</p>
            <Button onClick={fetchData} variant="secondary" className="mt-4">
              <RefreshCw className="w-4 h-4 mr-2" />
              Reintentar
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (!data) {
    return <EmptyState icon={Calendar} title="No hay datos disponibles" />
  }

  const palette = getArtemisPalette()
  const tokens = getArtemisChartTokens()
  const axisOptions = createArtemisAxisOptions()

  // Configuración gráfico por estado
  const pieBaseOptions = createArtemisBaseOptions({
    type: 'pie',
    colors: palette,
  })
  const chartEstadoOptions = {
    ...pieBaseOptions,
    labels: data.citasPorEstado.map(item => item.estado?.replace('_', ' ') || 'Sin estado'),
    colors: palette.slice(0, 6),
    stroke: {
      width: 2,
      colors: [tokens.isDark ? '#1a2535' : '#ffffff'],
    },
    legend: {
      ...pieBaseOptions.legend,
      position: 'bottom',
    },
  }

  const chartEstadoSeries = data.citasPorEstado.map(item => item.cantidad)

  // Configuración gráfico por tipo
  const typeBarBaseOptions = createArtemisBaseOptions({
    type: 'bar',
    height: 350,
    colors: [palette[1]],
  })
  const chartTipoOptions = {
    ...typeBarBaseOptions,
    xaxis: {
      ...axisOptions,
      categories: data.citasPorTipo.map(item => item.tipo),
    },
    yaxis: {
      ...axisOptions,
      tickAmount: 5,
    },
    plotOptions: {
      bar: {
        borderRadius: 8,
        columnWidth: '54%',
        horizontal: false,
      }
    },
    grid: {
      ...typeBarBaseOptions.grid,
      padding: { top: 4, right: 10, left: 0, bottom: 0 },
    },
  }

  const chartTipoSeries = [{
    name: 'Citas',
    data: data.citasPorTipo.map(item => item.cantidad)
  }]

  // Configuración gráfico mensual
  const areaBaseOptions = createArtemisBaseOptions({
    type: 'area',
    height: 350,
    colors: [palette[1], palette[0]],
  })
  const chartMensualOptions = {
    ...areaBaseOptions,
    xaxis: {
      ...axisOptions,
      categories: data.citasMensuales.map(item => item.mesCorto),
    },
    yaxis: {
      ...axisOptions,
      tickAmount: 5,
    },
    stroke: {
      curve: 'smooth',
      width: 3,
    },
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 0.6,
        opacityFrom: 0.35,
        opacityTo: 0.05,
      }
    },
    markers: {
      size: 3,
      hover: { size: 5 },
    },
    grid: {
      ...areaBaseOptions.grid,
      padding: { top: 6, right: 10, left: 0, bottom: 0 },
    },
    legend: {
      ...areaBaseOptions.legend,
      position: 'top',
    },
  }

  const chartMensualSeries = [
    {
      name: 'Total Citas',
      data: data.citasMensuales.map(item => item.total)
    },
    {
      name: 'Completadas',
      data: data.citasMensuales.map(item => item.completadas)
    }
  ]

  return (
    <div className="space-y-6">
      {/* Filtros */}
      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Filtros de Fecha</h3>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="Fecha Inicio"
              type="date"
              value={fechaInicio}
              onChange={(e) => setFechaInicio(e.target.value)}
            />
            <Input
              label="Fecha Fin"
              type="date"
              value={fechaFin}
              onChange={(e) => setFechaFin(e.target.value)}
            />
            <div className="flex items-end">
              <Button onClick={fetchData} variant="secondary" className="w-full">
                <RefreshCw className="w-4 h-4 mr-2" />
                Actualizar
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Resumen */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Citas</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">{data.totalCitas}</p>
              </div>
              <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <Calendar className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Citas en Período</p>
                <p className="text-3xl font-bold text-primary-600 dark:text-primary-400 mt-2">{data.citasEnPeriodo}</p>
              </div>
              <div className="p-3 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                <Clock className="w-8 h-8 text-primary-600 dark:text-primary-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Completadas en Período</p>
                <p className="text-3xl font-bold text-green-700 dark:text-green-400 mt-2">{data.citasCompletadasPeriodo}</p>
              </div>
              <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <CheckCircle className="w-8 h-8 text-green-700 dark:text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Citas por Estado</h3>
          </CardHeader>
          <CardContent>
            <Chart
              options={chartEstadoOptions}
              series={chartEstadoSeries}
              type="pie"
              height={350}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Citas por Tipo</h3>
          </CardHeader>
          <CardContent>
            <Chart
              options={chartTipoOptions}
              series={chartTipoSeries}
              type="bar"
              height={350}
            />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Tendencia Mensual</h3>
        </CardHeader>
        <CardContent>
          <Chart
            options={chartMensualOptions}
            series={chartMensualSeries}
            type="area"
            height={350}
          />
        </CardContent>
      </Card>

      {/* Top Clínicas */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Top Clínicas por Citas</h3>
            <Button variant="secondary" size="sm">
              <Download className="w-4 h-4 mr-2" />
              Exportar
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Clínica</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Total Citas</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {data.topClinicas.map((item, index) => (
                  <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">{item.clinica}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{item.cantidad}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

